import { supabase } from '@/integrations/supabase/client';
import { CalendarEvent } from '@/components/Calendar';
import { toast } from '@/components/ui/use-toast';

export interface CalendarEventRow {
  id: string;
  user_id: string;
  title: string;
  date: string;
  type: string;
  reminder: boolean;
  reminder_time: string | null;
  reminder_text: string | null;
  sms_reminder: boolean;
  sms_reminder_time: string | null;
  phone_number: string | null;
  created_at: string;
}

export const syncEventsWithSupabase = async (events: CalendarEvent[]): Promise<CalendarEvent[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data: existingEvents, error: fetchError } = await supabase
      .from('calendar_events')
      .select('*') as { data: CalendarEventRow[] | null, error: any };
      
    if (fetchError) {
      throw fetchError;
    }
    
    for (const event of events) {
      const existingEvent = existingEvents?.find(et => et.id === event.id);
      
      if (existingEvent) {
        const { error } = await supabase
          .from('calendar_events')
          .update({
            title: event.title,
            date: event.date,
            type: event.type,
            reminder: event.reminder || false,
            reminder_time: event.reminderTime,
            reminder_text: event.reminderText,
            sms_reminder: event.smsReminder || false,
            sms_reminder_time: event.smsReminderTime,
            phone_number: event.phoneNumber
          })
          .eq('id', event.id) as { error: any };
          
        if (error) {
          console.error('Error updating event:', error);
        }
      } else {
        const { error } = await supabase
          .from('calendar_events')
          .insert({
            id: event.id,
            user_id: user.id,
            title: event.title,
            date: event.date,
            type: event.type,
            reminder: event.reminder || false,
            reminder_time: event.reminderTime,
            reminder_text: event.reminderText,
            sms_reminder: event.smsReminder || false,
            sms_reminder_time: event.smsReminderTime,
            phone_number: event.phoneNumber
          }) as { error: any };
          
        if (error) {
          console.error('Error inserting event:', error);
        }
      }
    }
    
    const { data: updatedEvents, error: updatedError } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('user_id', user.id) as { data: CalendarEventRow[] | null, error: any };
      
    if (updatedError) {
      throw updatedError;
    }
    
    return updatedEvents?.map(event => ({
      id: event.id,
      title: event.title,
      date: event.date,
      type: event.type as "task" | "meeting" | "reminder" | "personal",
      reminder: event.reminder,
      reminderTime: event.reminder_time || undefined,
      reminderText: event.reminder_text || undefined,
      smsReminder: event.sms_reminder,
      smsReminderTime: event.sms_reminder_time || undefined,
      phoneNumber: event.phone_number || undefined
    })) || [];
  } catch (error) {
    console.error('Error syncing events with Supabase:', error);
    throw error;
  }
};

export const fetchUserEvents = async (): Promise<CalendarEvent[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('No authenticated user found when fetching calendar events');
      return [];
    }
    
    console.log('Fetching calendar events for user:', user.id);
    
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('user_id', user.id) as { data: CalendarEventRow[] | null, error: any };
      
    if (error) {
      console.error('Error fetching user events:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.log('No calendar events found in Supabase for user:', user.id);
      return [];
    }
    
    console.log(`Found ${data.length} calendar events in Supabase`);
    
    return data.map(event => ({
      id: event.id,
      title: event.title,
      date: event.date,
      type: event.type as "task" | "meeting" | "reminder" | "personal",
      reminder: event.reminder,
      reminderTime: event.reminder_time || undefined,
      reminderText: event.reminder_text || undefined,
      smsReminder: event.sms_reminder,
      smsReminderTime: event.sms_reminder_time || undefined,
      phoneNumber: event.phone_number || undefined
    }));
  } catch (error) {
    console.error('Error fetching user events:', error);
    throw error;
  }
};

export const mergeEvents = (localEvents: CalendarEvent[], supabaseEvents: CalendarEvent[]): CalendarEvent[] => {
  const mergedEvents = [...localEvents];
  const localEventIds = new Set(localEvents.map(event => event.id));
  
  for (const supabaseEvent of supabaseEvents) {
    if (!localEventIds.has(supabaseEvent.id)) {
      mergedEvents.push(supabaseEvent);
    }
  }
  
  return mergedEvents;
};

export const syncTasksToCalendarEvents = async (tasks: any[]): Promise<void> => {
  try {
    console.log("Syncing tasks to calendar events");
    
    const savedEvents = localStorage.getItem('calendar-events') || '[]';
    let existingEvents = JSON.parse(savedEvents);
    
    const existingTaskIds = new Set(
      existingEvents
        .filter((event: any) => event.linkedTaskId)
        .map((event: any) => event.linkedTaskId)
    );
    
    const tasksToSync = tasks.filter((task: any) => !existingTaskIds.has(task.id));
    
    if (tasksToSync.length === 0) {
      console.log("No new tasks to sync to calendar");
      return;
    }
    
    console.log(`Found ${tasksToSync.length} tasks to sync to calendar`);
    
    const newEvents = tasksToSync.map((task: any) => {
      let eventDate;
      try {
        const [hours, minutes] = task.scheduledTime.split(':').map(Number);
        const dateObj = new Date();
        dateObj.setHours(hours);
        dateObj.setMinutes(minutes);
        eventDate = dateObj.toISOString().split('T')[0];
      } catch (e) {
        console.error("Error formatting date for task:", task.id, e);
        eventDate = new Date().toISOString().split('T')[0];
      }
      
      return {
        id: crypto.randomUUID(),
        linkedTaskId: task.id,
        title: task.title,
        date: eventDate,
        type: 'task',
        reminder: task.reminderSet || false,
        reminderTime: task.scheduledTime ? `${eventDate}T${task.scheduledTime}:00` : undefined,
        reminderText: `Task duration: ${task.duration} minutes`,
        completed: task.completed || false
      };
    });
    
    existingEvents = existingEvents.map((event: any) => {
      if (event.linkedTaskId) {
        const relatedTask = tasks.find((task: any) => task.id === event.linkedTaskId);
        if (relatedTask) {
          return {
            ...event,
            title: relatedTask.title,
            completed: relatedTask.completed || false
          };
        }
      }
      return event;
    });
    
    const updatedEvents = [...existingEvents, ...newEvents];
    localStorage.setItem('calendar-events', JSON.stringify(updatedEvents));
    
    const event = new CustomEvent('calendar-events-updated');
    window.dispatchEvent(event);
    
    console.log(`Successfully synced ${newEvents.length} tasks to calendar events`);
  } catch (error) {
    console.error("Error syncing tasks to calendar:", error);
  }
};

export const fetchEvent = async (eventId: string): Promise<CalendarEvent | null> => {
  try {
    const localEvents = JSON.parse(localStorage.getItem('calendar-events') || '[]');
    const localEvent = localEvents.find((event: any) => event.id === eventId);
    
    if (localEvent) {
      console.log('Found event in local storage:', eventId);
      return localEvent;
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('id', eventId)
      .eq('user_id', user.id)
      .single() as { data: CalendarEventRow | null, error: any };
      
    if (error) {
      throw error;
    }
    
    if (!data) {
      return null;
    }
    
    return {
      id: data.id,
      title: data.title,
      date: data.date,
      type: data.type as "task" | "meeting" | "reminder" | "personal",
      reminder: data.reminder,
      reminderTime: data.reminder_time || undefined,
      reminderText: data.reminder_text || undefined,
      smsReminder: data.sms_reminder,
      smsReminderTime: data.sms_reminder_time || undefined,
      phoneNumber: data.phone_number || undefined
    };
  } catch (error) {
    console.error('Error fetching event:', error);
    throw error;
  }
};

export const updateEvent = async (event: CalendarEvent): Promise<CalendarEvent> => {
  try {
    const localEvents = JSON.parse(localStorage.getItem('calendar-events') || '[]');
    const updatedLocalEvents = localEvents.map((e: any) => 
      e.id === event.id ? event : e
    );
    
    localStorage.setItem('calendar-events', JSON.stringify(updatedLocalEvents));
    
    const updateEvent = new CustomEvent('calendar-events-updated');
    window.dispatchEvent(updateEvent);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('User not authenticated, event only updated in local storage');
      return event;
    }
    
    const { error } = await supabase
      .from('calendar_events')
      .update({
        title: event.title,
        date: event.date,
        type: event.type,
        reminder: event.reminder || false,
        reminder_time: event.reminderTime,
        reminder_text: event.reminderText,
        sms_reminder: event.smsReminder || false,
        sms_reminder_time: event.smsReminderTime,
        phone_number: event.phoneNumber
      })
      .eq('id', event.id)
      .eq('user_id', user.id) as { error: any };
      
    if (error) {
      throw error;
    }
    
    return event;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  try {
    const localEvents = JSON.parse(localStorage.getItem('calendar-events') || '[]');
    const filteredEvents = localEvents.filter((e: any) => e.id !== eventId);
    localStorage.setItem('calendar-events', JSON.stringify(filteredEvents));
    
    const updateEvent = new CustomEvent('calendar-events-updated');
    window.dispatchEvent(updateEvent);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('User not authenticated, event only deleted from local storage');
      return;
    }
    
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', eventId)
      .eq('user_id', user.id);
      
    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

export const sendSmsReminder = async (eventId: string): Promise<any> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const event = await fetchEvent(eventId);
    
    if (!event) {
      throw new Error('Event not found');
    }
    
    if (!event.smsReminder || !event.phoneNumber) {
      throw new Error('SMS reminder not enabled or phone number missing');
    }
    
    console.log('Sending SMS reminder for event:', event.title);
    
    const { data, error } = await supabase.functions.invoke('send-sms', {
      body: {
        eventId: event.id,
        userId: user.id
      }
    });
    
    if (error) {
      console.error('Error invoking SMS function:', error);
      throw error;
    }
    
    console.log('SMS reminder sent successfully:', data);
    return data;
  } catch (error) {
    console.error('Error sending SMS reminder:', error);
    throw error;
  }
};
